import { offerPolicy, productNodeId } from "../../scripts/offer-policy.mjs";
import { ORGANIZATION_ID, ORGANIZATION_SAME_AS, organizationSchema, webSiteSchema } from "../../scripts/site-identity.mjs";
import { Helmet } from "react-helmet-async";

interface OrganizationJsonLdProps {
  name: string;
  url: string;
  logo?: string;
  description?: string;
}

// Only claims that can be verified are published here: name, url, logo,
// description and a real contact address. No telephone (the previous number was
// in the reserved fictional 555-01xx range), no postal address, no founding
// date and no opening hours. `sameAs` lists only profiles confirmed to be
// LoftLoom's own (Pinterest, domain-claimed 2026-09-24); an unconfirmed one
// would merge this entity with someone else's. The id and the list live in
// scripts/site-identity.mjs, which the prerendered homepage reads too.
export { ORGANIZATION_ID, ORGANIZATION_SAME_AS };
export const OrganizationJsonLd = ({ name, url, logo, description }: OrganizationJsonLdProps) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name,
    url,
    ...(logo && { logo }),
    ...(description && { description }),
    email: "hello@loftloom.com",
    sameAs: ORGANIZATION_SAME_AS,
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface WebSiteJsonLdProps {
  name: string;
  url: string;
}

export const WebSiteJsonLd = ({ name, url }: WebSiteJsonLdProps) => {
  // Same object as the prerendered homepage's block (scripts/site-identity.mjs).
  const data = webSiteSchema(name, url);

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface ProductJsonLdProps {
  name: string;
  description: string;
  image?: string;
  price: string;
  currency: string;
  availability: boolean;
  url: string;
  ratingValue?: number;
  reviewCount?: number;
  sku?: string;
  brand?: string;
  compareAtPrice?: string;
  category?: string;
}

export const ProductJsonLd = ({ 
  name, 
  description, 
  image, 
  price, 
  currency, 
  availability, 
  url, 
  ratingValue, 
  reviewCount,
  sku,
  brand,
  category
}: ProductJsonLdProps) => {
  const data: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Product",
    // Same node as the prerendered Product block, so Google merges the two.
    "@id": productNodeId(url),
    name,
    description: description.slice(0, 500),
    // An array, as in the prerendered block (scripts/prerender-body.mjs).
    ...(image && { image: [image] }),
    url,
    // No brand: LoftLoom sells these pieces but does not make them, and the
    // supplier titles don't name the maker.
    ...(brand && { brand: { "@type": "Brand", name: brand } }),
    ...(sku && { sku }),
    ...(category && { category }),
    offers: {
      "@type": "Offer",
      price,
      priceCurrency: currency,
      availability: availability
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      url,
      // Shipping, delivery and returns: same source as the prerendered block.
      ...offerPolicy(price, currency),
    },
  };

  if (ratingValue && ratingValue > 0 && reviewCount && reviewCount > 0) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: ratingValue.toFixed(1),
      reviewCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface LocalBusinessJsonLdProps {
  name?: string;
  url?: string;
}

// Deliberately an Organization, NOT a FurnitureStore: FurnitureStore is a
// LocalBusiness subtype and asserts premises a customer can visit. There are
// none. No phone, street address, founding date or opening hours. Same @id as
// OrganizationJsonLd, so the two blocks describe one entity.
export const LocalBusinessJsonLd = ({
  name = "LoftLoom",
  url = "https://loftloom.com"
}: LocalBusinessJsonLdProps) => {
  // Same object as the prerendered homepage's block (scripts/site-identity.mjs).
  const data = organizationSchema(name, url);

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface BreadcrumbJsonLdProps {
  items: { name: string; url?: string }[];
}

export const BreadcrumbJsonLd = ({ items }: BreadcrumbJsonLdProps) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      ...(item.url && { item: item.url }),
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface FAQJsonLdProps {
  questions: { question: string; answer: string }[];
}

export const FAQJsonLd = ({ questions }: FAQJsonLdProps) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};
